import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Calendar, Clock, MapPin, Shirt } from "lucide-react";
import { fetchEvent, FALLBACK_TICKET_URL } from "../lib/api";
import { useLang, loc, locList, T } from "../lib/i18n";
import { RevealLines, FadeUp } from "../components/site/RevealText";
import { Lightbox } from "../components/site/Lightbox";
import { MarqueeStrip } from "../components/site/MarqueeStrip";

const InfoCell = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="border border-slate-900/10 rounded-2xl p-6 md:p-8 bg-white">
    <Icon size={20} className="text-[#00B4D8]" />
    <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 mt-4">{label}</p>
    <p className="text-slate-900 text-base md:text-lg mt-1.5 leading-snug">{value}</p>
  </div>
);

export default function EventPage({ settings }) {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const { lang } = useLang();
  const t = T[lang].event;

  useEffect(() => {
    setEv(null);
    setNotFound(false);
    fetchEvent(slug).then(setEv).catch(() => setNotFound(true));
  }, [slug]);

  const url = settings?.ticket_url || FALLBACK_TICKET_URL;

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6">
        <h1 className="font-serif text-4xl text-slate-900">{t.notFound}</h1>
        <Link to="/" data-testid="notfound-home-link" className="text-[#00B4D8] uppercase text-xs tracking-[0.2em] border-b border-[#00B4D8] pb-1">{t.back}</Link>
      </div>
    );

  if (!ev)
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="event-loading">
        <div className="w-10 h-10 rounded-full border-2 border-[#00B4D8] border-t-transparent animate-spin" />
      </div>
    );

  return (
    <div data-testid={`event-page-${ev.slug}`}>
      <Lightbox src={lightbox} alt={ev.title} onClose={() => setLightbox(null)} />
      <section className="relative min-h-[88svh] flex items-end overflow-hidden">
        <img src={ev.hero_image} alt={ev.title} className="absolute inset-0 w-full h-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2530]/85 via-black/25 to-black/30" />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 pb-16 md:pb-24 pt-40 w-full">
          <Link to="/" data-testid="event-back-link" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-[0.2em] mb-8 transition-colors duration-300">
            <ArrowLeft size={14} /> {t.all}
          </Link>
          <p className="text-white/80 text-xs md:text-sm uppercase tracking-[0.35em] mb-5">{loc(ev, "date", lang)} · {ev.venue}</p>
          <RevealLines key={lang} lines={[ev.title]} className="font-serif text-white text-5xl sm:text-6xl md:text-8xl tracking-tight leading-[1.0]" />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }} className="text-white/75 text-lg md:text-xl mt-5 max-w-xl font-light italic font-serif">
            {loc(ev, "tagline", lang)}
          </motion.p>
          <motion.a
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            href={url}
            target="_blank"
            rel="noopener"
            data-testid="event-hero-buy-tickets"
            className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#00B4D8] hover:bg-[#33c5e2] text-white text-sm uppercase tracking-[0.2em] px-9 py-4 transition-colors duration-300"
          >
            {t.buy}
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </motion.a>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <FadeUp className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[#00B4D8] mb-5">{t.kicker}</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-slate-900 leading-[1.08]">{t.about}</h2>
            <p data-testid="event-description" className="text-slate-500 text-base md:text-lg leading-relaxed mt-7 whitespace-pre-line">{loc(ev, "description", lang)}</p>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 content-start">
            <InfoCell icon={Calendar} label={t.date} value={loc(ev, "date", lang)} testid="event-info-date" />
            <InfoCell icon={Clock} label={t.time} value={loc(ev, "time", lang)} testid="event-info-time" />
            <InfoCell icon={MapPin} label={t.venue} value={ev.venue} testid="event-info-venue" />
            <InfoCell icon={Shirt} label={t.dress} value={loc(ev, "dress_code", lang)} testid="event-info-dresscode" />
          </FadeUp>
        </div>
      </section>

      <section className="bg-[#0B2530] text-white py-20 md:py-32">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-[#7BDFF2] mb-5">{t.lineupKicker}</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.08] mb-10">{t.lineup}</h2>
            <div data-testid="event-lineup" className="flex flex-col">
              {(ev.lineup || []).map((a, i) => (
                <div key={i} className="flex items-baseline gap-6 py-6 border-b border-white/10 group">
                  <span className="font-serif italic text-2xl md:text-3xl text-[#7BDFF2]/40 w-12">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="font-serif text-2xl md:text-3xl tracking-tight transition-colors duration-300 group-hover:text-[#7BDFF2]">{a.name}</p>
                    <p className="text-white/40 text-sm mt-1 uppercase tracking-[0.15em]">{a.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#7BDFF2] mb-5">{t.programKicker}</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.08] mb-10">{t.program}</h2>
            <div data-testid="event-program" className="flex flex-col">
              {locList(ev, "program", lang).map((p, i) => (
                <div key={i} className="flex gap-6 py-5 border-b border-white/10">
                  <span className="text-[#7BDFF2] text-sm uppercase tracking-[0.15em] w-24 shrink-0 pt-0.5">{p.time}</span>
                  <p className="text-white/80 text-base md:text-lg">{p.item}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <MarqueeStrip />

      <section className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.3em] text-[#00B4D8] mb-5">{t.galleryKicker}</p>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-slate-900 leading-[1.08] mb-12">{t.gallery}</h2>
        </FadeUp>
        <div data-testid="event-gallery" className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {(ev.gallery || []).map((g, i) => (
            <FadeUp key={i} delay={i * 0.08} className={i % 3 === 0 ? "col-span-2 md:col-span-2" : ""}>
              <button
                type="button"
                data-testid={`event-gallery-item-${i}`}
                onClick={() => setLightbox(g)}
                className="block w-full overflow-hidden rounded-2xl group cursor-zoom-in"
              >
                <img src={g} alt={`${ev.title} gallery ${i + 1}`} loading="lazy" className="w-full h-56 md:h-80 object-cover transition-transform duration-700 group-hover:scale-105" />
              </button>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-stretch">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-[#00B4D8] mb-5">{t.gettingThere}</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-slate-900 leading-[1.08]">{t.theVenue}</h2>
            <p data-testid="event-venue-address" className="text-slate-500 text-base md:text-lg mt-6">{ev.venue_address}</p>
            <a
              href={url}
              target="_blank"
              rel="noopener"
              data-testid="event-bottom-buy-tickets"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-[#00B4D8] hover:bg-[#0096b4] text-white text-sm uppercase tracking-[0.2em] px-9 py-4 transition-colors duration-300"
            >
              {t.buy}
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="overflow-hidden rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.08)] h-[320px] md:h-full min-h-[320px]">
              <iframe
                data-testid="event-map"
                title={`Map — ${ev.venue}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(ev.map_query || ev.venue_address)}&output=embed`}
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
